/**
 * Typed client for the SSO endpoints (item 7.7, spec 06 §2.1).
 *
 * Two audiences:
 *  - PUBLIC sign-in: `listSsoProviders` (which providers to show on the login
 *    page) and `startSso` (begin the redirect to Google/Microsoft).
 *  - ADMIN config: read/update/validate the provider configuration.
 *
 * The encrypted client secret is WRITE-ONLY: it is sent on update but never
 * returned — reads expose only `has_secret`. Transport only (spec 08 §1).
 */

import { apiFetch } from '../auth/apiClient';

/** A provider a visitor can sign in with (public login page). */
export interface SsoProviderSummary {
  /** Provider token: 'google' | 'microsoft'. */
  provider: string;
  /** Human label for the button, e.g. "Google". */
  label: string;
}

/** One provider's admin configuration (secret never exposed). */
export interface SsoProviderConfig {
  provider: string;
  enabled: boolean;
  client_id: string | null;
  redirect_uri: string | null;
  satisfies_mfa: boolean;
  /** Whether a client secret is configured — presence only, never the value. */
  has_secret: boolean;
  last_tested_at: string | null;
  last_test_ok: boolean | null;
  last_test_message: string | null;
  updated_at: string | null;
}

/** Body for `PATCH /auth/sso/config/{provider}`. */
export interface UpdateSsoConfigInput {
  enabled?: boolean;
  client_id?: string | null;
  redirect_uri?: string | null;
  satisfies_mfa?: boolean;
  /** New secret; omit to leave unchanged, empty string to clear. */
  client_secret?: string;
}

/** Outcome of the admin "validate configuration" action. */
export interface SsoTestResult {
  ok: boolean;
  message: string;
  tested_at: string | null;
}

interface DataEnvelope<T> {
  data: T;
}

/** The providers a visitor can sign in with (enabled + fully configured). */
export async function listSsoProviders(): Promise<SsoProviderSummary[]> {
  const response = await apiFetch<DataEnvelope<SsoProviderSummary[]>>('/auth/sso/providers');
  return response.data;
}

/**
 * Full-page navigation seam. Wrapped in an object so it can be spied/stubbed in
 * tests (jsdom makes `window.location.assign` read-only), keeping {@link startSso}
 * honest about actually redirecting the browser in production.
 */
export const ssoNavigator = {
  redirect(url: string): void {
    window.location.assign(url);
  },
};

/**
 * Begin an SSO sign-in: ask the backend for the provider authorization URL, then
 * hand the browser off to it. The provider will redirect back to
 * `/login/sso/callback` with the bearer token in the URL fragment.
 */
export async function startSso(provider: string): Promise<void> {
  const response = await apiFetch<DataEnvelope<{ authorize_url: string }>>(
    `/auth/sso/${provider}/redirect`,
  );
  ssoNavigator.redirect(response.data.authorize_url);
}

/** Read the admin SSO configuration for every supported provider. */
export async function getSsoConfig(): Promise<SsoProviderConfig[]> {
  const response = await apiFetch<DataEnvelope<SsoProviderConfig[]>>('/auth/sso/config');
  return response.data;
}

/** Update one provider's configuration. Resolves with the updated provider. */
export async function updateSsoConfig(
  provider: string,
  input: UpdateSsoConfigInput,
): Promise<SsoProviderConfig> {
  const response = await apiFetch<DataEnvelope<SsoProviderConfig>>(`/auth/sso/config/${provider}`, {
    method: 'PATCH',
    body: input,
  });
  return response.data;
}

/** Validate one provider's stored configuration (no live IdP round-trip). */
export async function testSsoConfig(provider: string): Promise<SsoTestResult> {
  const response = await apiFetch<DataEnvelope<SsoTestResult>>(`/auth/sso/config/${provider}/test`, {
    method: 'POST',
  });
  return response.data;
}
